import { Group, Paper, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Sticky } from "@/components/layouts"
import { UserLine } from "@/components/people"
import { AppLoader } from "@/components/utils"
import { useAPIAnswer, useAPISurveySessionPeers } from "@/hooks"
import { Question } from "../questions"
import { ButtonSurvey } from "./StageButton"
import { StageControls } from "./StageControls"
import type { IStageQuestionProps } from "./types"
import { validateQuestion } from "./utils"

export function QuestionsFeedback({ answers, questions, usersession, onNext, onStep }: IStageQuestionProps) {
  const { t } = useTranslation()

  // Load peers
  const { data: peers = [], isLoading } = useAPISurveySessionPeers({
    sessionId: usersession.session.id,
  })

  // Skip the stage if there are no peers
  useEffect(() => {
    if (!isLoading && !peers.length) onNext()
  }, [questions, onNext, isLoading])

  // Get the current step data
  const [step, setStep] = useState(0)
  const currentPeer = peers[step]
  const currentAnswers = answers.filter((a) => a.peer_id === currentPeer?.id)

  const initialValues: any = {}

  for (const question of questions) {
    const answer = currentAnswers.find((a) => a.question_id === question.id)
    initialValues[question.id] = {
      value: answer?.value,
      comment: answer?.comment,
    }
  }

  const form = useForm<{ [k: string]: { value: number | null; comment: string | null } }>({
    validateInputOnBlur: true,
    validateInputOnChange: true,
    initialValues: Object.fromEntries(questions.map((question) => [question.id, { value: null, comment: null }])),
    validate: (values) => {
      const res = {}
      questions.forEach((question) => {
        const { value, comment } = values[question.id]!
        const error = validateQuestion(question, value, comment, t)
        if (error) res[question.id] = error.value ?? error.comment
      })
      return res
    },
  })

  useEffect(() => {
    if (!currentPeer) return
    form.setValues(
      Object.fromEntries(
        questions.map((question) => [
          question.id,
          {
            value: currentAnswers.find((a) => a.question_id === question.id)?.value ?? null,
            comment: currentAnswers.find((a) => a.question_id === question.id)?.comment ?? null,
          },
        ]),
      ),
    )
    form.resetDirty()
  }, [currentPeer])

  const updateStep = (newStep: number) => {
    setStep(newStep)
    onStep({
      step: newStep,
      peer: currentPeer,
      steps: peers.length,
    })
  }

  const { create, isUpdating } = useAPIAnswer(usersession.session.id)
  const isReady = !isUpdating && form.isValid()
  const saveAnswers = () =>
    create(
      Object.keys(form.values).map((question_id) => ({
        ...currentAnswers.find((a) => a.question_id === question_id),
        question_id,
        peer_id: currentPeer?.id,
        usersession_id: usersession.id,
        value: form.values[question_id]?.value,
        comment: form.values[question_id]?.comment,
      })),
    )

  if (isLoading || !peers || !currentPeer) return <AppLoader />

  return (
    <>
      <Sticky bg="none">
        <Paper radius={0} style={{ borderTop: "none", borderBottom: "1px solid var(--mantine-color-gray-3)" }} mb="md">
          <Group wrap="nowrap" gap="md">
            <UserLine p="md" user={currentPeer} />
            <Text color="dimmed" ml="auto" fz={22} fw={500}>
              {step + 1}/{peers.length}
            </Text>
          </Group>
        </Paper>
      </Sticky>

      {questions.map((question, idx) => (
        <Question
          key={question.id}
          question={question}
          autoFocus={idx === 0}
          field={form.getInputProps(question.id)}
          value={form.values[question.id]?.value}
          comment={form.values[question.id]?.comment}
          setValue={(value: number | null) =>
            form.setFieldValue(question.id, { ...(form.values[question.id] as any), value: value ?? null })
          }
          setComment={(comment: string | null) =>
            form.setFieldValue(question.id, { ...(form.values[question.id] as any), comment: comment ?? null })
          }
        />
      ))}

      <StageControls>
        {/* Previous button is only shown on questions that are not the first question */}
        <ButtonSurvey variant="navy" disabled={!step} onClick={() => updateStep(step - 1)} mr="auto" flex={1}>
          {t("Previous")}
        </ButtonSurvey>
        {/* Next button is only shown on questions that are not the last question */}
        <ButtonSurvey
          flex={2}
          disabled={!isReady}
          hidden={step === peers.length - 1}
          variant={form.isDirty() ? "save" : "navy"}
          onClick={() => {
            if (form.isDirty())
              saveAnswers().then(() => {
                form.resetDirty()
              })
            updateStep(step + 1)
          }}
        >
          {`${t("Next")} (${step + 1}/${peers.length})`}
        </ButtonSurvey>
        {/* Finish button is only shown on the last question */}
        <ButtonSurvey
          flex={2}
          disabled={!isReady}
          variant="save"
          hidden={step !== peers.length - 1}
          onClick={async () => {
            if (form.isDirty()) {
              await saveAnswers()
              form.resetDirty()
              onNext?.()
            } else onNext?.()
          }}
        >
          {t("Finish")}
        </ButtonSurvey>
      </StageControls>
    </>
  )
}
